import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { CheckCircle2, XCircle, Star, MessageSquare, Trash2, Copy, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useSettings } from '@/context/SettingsContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_LABELS = {
  sent: { label: 'Sent', cls: 'bg-blue-100 text-blue-700' },
  submitted: { label: 'Awaiting Review', cls: 'bg-amber-100 text-amber-700' },
  accepted: { label: 'Published', cls: 'bg-green-100 text-green-700' },
  declined: { label: 'Declined', cls: 'bg-red-100 text-red-700' },
};

const TABS = [
  { id: 'submitted', label: 'Awaiting Review' },
  { id: 'sent', label: 'Sent' },
  { id: 'accepted', label: 'Published' },
  { id: 'declined', label: 'Declined' },
];

const AdminFeedback = () => {
  const { settings } = useSettings();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('submitted');

  const fetchAll = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('admin_token');
      const res = await axios.get(`${API}/feedback-requests`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setItems(res.data || []);
    } catch (err) {
      toast.error('Failed to load feedback');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const buildLink = (id) => `${window.location.origin}/feedback/${id}`;
  const buildWhatsappLink = (item) => {
    const link = buildLink(item.id);
    const msg =
      `Hi ${item.customer_name}! We'd love your feedback on ${item.project_name}. ` +
      `Please take a minute to share your experience here: ${link}\n\nThank you,\nInfinity Group`;
    const num = ((item.whatsapp_number || settings.admin_whatsapp_number || '') || '').replace(/[^0-9]/g, '');
    return num
      ? `https://wa.me/${num}?text=${encodeURIComponent(msg)}`
      : `https://wa.me/?text=${encodeURIComponent(msg)}`;
  };

  const copyLink = (id) => {
    navigator.clipboard.writeText(buildLink(id));
    toast.success('Link copied to clipboard');
  };

  const accept = async (id) => {
    if (!window.confirm('Publish this feedback as a testimonial on the homepage?')) return;
    try {
      const token = localStorage.getItem('admin_token');
      await axios.post(`${API}/feedback-requests/${id}/accept`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Published as testimonial');
      fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to accept');
    }
  };

  const decline = async (id) => {
    if (!window.confirm('Decline this feedback?')) return;
    try {
      const token = localStorage.getItem('admin_token');
      await axios.post(`${API}/feedback-requests/${id}/decline`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Feedback declined');
      fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to decline');
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this feedback request? This cannot be undone.')) return;
    try {
      const token = localStorage.getItem('admin_token');
      await axios.delete(`${API}/feedback-requests/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Deleted');
      fetchAll();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const filtered = items.filter((it) => it.status === activeTab);
  const counts = items.reduce((acc, it) => {
    acc[it.status] = (acc[it.status] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-gray-50">
      <nav className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/admin/dashboard" className="text-xl font-bold text-gray-900">Infinity Group</Link>
            <div className="flex items-center gap-4 text-sm">
              <Link to="/admin/dashboard" className="text-gray-600 hover:text-gray-900">Dashboard</Link>
              <Link to="/admin/enquiries" className="text-gray-600 hover:text-gray-900">Enquiries</Link>
              <Link to="/admin/feedback" className="text-blue-600 font-medium">Feedback</Link>
              <Link to="/admin/testimonials" className="text-gray-600 hover:text-gray-900">Reviews</Link>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Customer Feedback</h1>
          <p className="text-gray-500 mt-1">Manage feedback links and review submitted feedback before publishing.</p>
        </div>

        <div className="flex gap-2 mb-6 border-b border-gray-200 overflow-x-auto">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setActiveTab(t.id)}
              className={`px-4 py-2 text-sm font-medium whitespace-nowrap transition-colors ${
                activeTab === t.id
                  ? 'border-b-2 border-blue-600 text-blue-600'
                  : 'text-gray-500 hover:text-gray-800'
              }`}
              data-testid={`feedback-tab-${t.id}`}
            >
              {t.label} ({counts[t.id] || 0})
            </button>
          ))}
        </div>

        {loading ? (
          <div className="text-center py-12 text-gray-400">Loading...</div>
        ) : filtered.length === 0 ? (
          <div className="bg-white rounded-xl p-10 text-center text-gray-400">
            <MessageSquare size={48} className="mx-auto mb-3 text-gray-300" />
            <p>No items in this tab.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {filtered.map((item) => {
              const meta = STATUS_LABELS[item.status] || STATUS_LABELS.sent;
              return (
                <div
                  key={item.id}
                  className="bg-white rounded-xl p-5 shadow-sm border border-gray-100"
                  data-testid={`feedback-card-${item.id}`}
                >
                  <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold text-gray-900">{item.customer_name}</h3>
                        <span className={`px-2 py-0.5 text-xs rounded-full ${meta.cls}`}>{meta.label}</span>
                        <span className="px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-700">
                          {item.feedback_type}
                        </span>
                      </div>
                      <p className="text-sm text-gray-500">
                        Project: <strong>{item.project_name}</strong>
                        {item.whatsapp_number ? ` · WhatsApp: ${item.whatsapp_number}` : ''}
                      </p>
                    </div>
                    <button
                      onClick={() => remove(item.id)}
                      className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                      title="Delete"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>

                  {item.status === 'sent' && (
                    <div className="flex flex-wrap gap-2">
                      <Button
                        size="sm"
                        onClick={() => copyLink(item.id)}
                        className="bg-gray-100 text-gray-800 hover:bg-gray-200"
                      >
                        <Copy size={14} className="mr-1" /> Copy Link
                      </Button>
                      <a
                        href={buildWhatsappLink(item)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-md bg-green-600 hover:bg-green-700 text-white"
                        data-testid={`share-whatsapp-${item.id}`}
                      >
                        <Send size={14} className="mr-1" /> Share on WhatsApp
                      </a>
                    </div>
                  )}

                  {item.status === 'submitted' && (
                    <div className="bg-gray-50 rounded-lg p-4 mt-2">
                      <div className="flex items-center gap-1 mb-2">
                        {[1, 2, 3, 4, 5].map((n) => (
                          <Star
                            key={n}
                            size={18}
                            className={n <= (item.response_rating || 0) ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}
                          />
                        ))}
                        <span className="ml-2 text-sm text-gray-600">{item.response_rating || 0}/5</span>
                      </div>
                      <p className="text-gray-800 italic mb-3">"{item.response_text}"</p>
                      {item.response_photo && (
                        <img src={item.response_photo} alt="customer" className="max-h-48 rounded-lg border mb-3" />
                      )}
                      <div className="flex flex-wrap gap-2">
                        <Button
                          size="sm"
                          onClick={() => accept(item.id)}
                          className="bg-green-600 hover:bg-green-700"
                          data-testid={`accept-feedback-${item.id}`}
                        >
                          <CheckCircle2 size={14} className="mr-1" /> Accept & Publish
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => decline(item.id)}
                          className="border-red-300 text-red-700 hover:bg-red-50"
                          data-testid={`decline-feedback-${item.id}`}
                        >
                          <XCircle size={14} className="mr-1" /> Decline
                        </Button>
                      </div>
                    </div>
                  )}

                  {(item.status === 'accepted' || item.status === 'declined') && item.response_text && (
                    <div className="bg-gray-50 rounded-lg p-3 mt-2 text-sm text-gray-700">
                      <div className="flex items-center gap-1 mb-1">
                        {[1, 2, 3, 4, 5].map((n) => (
                          <Star
                            key={n}
                            size={14}
                            className={n <= (item.response_rating || 0) ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}
                          />
                        ))}
                      </div>
                      <p className="italic">"{item.response_text}"</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminFeedback;
